import {
  EncountersTemplate,
  type EncountersTemplateEncounter,
} from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import { buildSingleItemPdfEntry } from './pdfOverride';

type EncountersRecordsKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_RECORDS; type: DocumentType.ENCOUNTERS }
>;

interface RawCuratedEncounter {
  id: string;
  encounterText?: string;
  typeName?: string;
  startTime?: string;
  endTime?: string;
  hospitalizationDischargeDisposition?: string;
  documentReferences: Array<{
    referenceType?: string;
    contentType?: string;
    signedUrl?: string;
  }>;
  locations: Array<{
    name?: string;
    locationType?: string;
    addressUse?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  }>;
  conditions: Array<{
    conditionName?: string;
    conditionOnsetDate?: string;
    conditionRecordedDate?: string;
    conditionClinicalStatus?: string;
    conditionCategoryCodeName?: string;
    conditionText?: string;
  }>;
  practitioners: Array<{
    referenceId?: string;
    givenName?: string;
    familyName?: string;
    addressStreet?: string;
    addressCity?: string;
    addressState?: string;
    addressUse?: string;
    roleSpecialty?: string;
    role?: string;
    telecoms: string[];
  }>;
}

interface RawEncounter {
  id: string;
  name: string;
  dateOfStudy?: string;
  sourceType: string;
  curated: RawCuratedEncounter;
}

export interface EncountersRecordsQueryData {
  encounters: RawEncounter[];
}

const buildEncounter = (raw: RawEncounter): EncountersTemplateEncounter => ({
  id: raw.id,
  name: raw.name,
  encounterText: raw.curated.encounterText,
  typeName: raw.curated.typeName,
  startTime: raw.curated.startTime,
  endTime: raw.curated.endTime,
  hospitalizationDischargeDisposition:
    raw.curated.hospitalizationDischargeDisposition,
  documentReferences: raw.curated.documentReferences,
  locations: raw.curated.locations,
  conditions: raw.curated.conditions,
  practitioners: raw.curated.practitioners,
});

export const adaptEncountersRecords = (
  data: EncountersRecordsQueryData,
  key: EncountersRecordsKey & { highlightEncounterId?: string },
): DocumentEntry | undefined => {
  const encounters = data?.encounters ?? [];
  if (encounters.length === 0) return undefined;

  const pdfEntry = buildSingleItemPdfEntry(
    encounters.length === 1 ? encounters[0].curated.documentReferences : [],
    (documentReference) => [
      {
        signedUrl: documentReference.signedUrl,
        contentType: documentReference.contentType,
        title: key.name,
      },
    ],
    { title: key.name },
  );
  if (pdfEntry) return pdfEntry;

  const adaptedEncounters = encounters.map(buildEncounter);

  return {
    kind: 'template',
    title: key.name,
    render: (ctx) => (
      <EncountersTemplate
        encounters={adaptedEncounters}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightEncounterId={key.highlightEncounterId}
      />
    ),
  };
};
